import React from 'react';
import { LuCopy, LuEllipsis, LuPencil, LuSquare, LuTrash2 } from 'react-icons/lu';

import { StrategyType, StrategyWithUsage } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { LibrarySelectionCheckbox } from '../LibrarySelectionCheckbox';
import { StackedCard } from '../StackedCard';
import { TypeBadge } from '../TypeBadge';
import { getStrategyHoldingsLabel, getStrategyTypeBadge } from './strategy-utils';

interface StrategyCardProps {
    strategy: StrategyWithUsage;
    canEdit: boolean;
    selected: boolean;
    deleteBlockedReason?: string;
    getAssetInfo: (securityId: string) => AssetInfo | undefined;
    onToggleSelect: () => void;
    onEdit: () => void;
    onClone: () => void;
    onDelete: () => void;
}

const HOLDINGS_PREVIEW_COUNT = 3;

export const StrategyCard: React.FC<StrategyCardProps> = ({
    strategy,
    canEdit,
    selected,
    deleteBlockedReason,
    getAssetInfo,
    onToggleSelect,
    onEdit,
    onClone,
    onDelete,
}) => {
    const badge = getStrategyTypeBadge(strategy);
    const isOwned = strategy.strategyType === StrategyType.PERSONAL;
    const hasReferencingAllocations = (strategy.referencingAllocationCount ?? 0) > 0;

    const sortedAssetAllocations = [...strategy.assetAllocations].sort(
        (a, b) => parseFloat(b.weight) - parseFloat(a.weight),
    );
    const holdingsPreview = sortedAssetAllocations
        .slice(0, HOLDINGS_PREVIEW_COUNT)
        .map((asset) => getAssetInfo(asset.securityId)?.symbol || asset.securityId);
    const hiddenHoldingsCount = sortedAssetAllocations.length - holdingsPreview.length;

    return (
        <StackedCard stacked={false}>
            <div className="mb-3 flex items-center gap-2.5">
                <LibrarySelectionCheckbox
                    label={`Select strategy ${strategy.strategyName} for deletion`}
                    checked={selected}
                    onToggle={onToggleSelect}
                    disabledReason={deleteBlockedReason}
                />
                <TypeBadge label="Strategy" tone="strategy" icon={<LuSquare className="h-4 w-4" />} />
                {canEdit && (
                    <DropdownMenu>
                        <DropdownMenuTrigger className="text-cocoa-500 hover:bg-cocoa-50 ml-auto flex h-7 w-7 items-center justify-center rounded-md outline-none">
                            <LuEllipsis className="h-5 w-5" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="tracking-[-0.02em]">
                            <DropdownMenuItem onClick={onEdit} className="flex items-center gap-2">
                                <LuPencil className="h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={onClone} className="flex items-center gap-2">
                                <LuCopy className="h-4 w-4" /> Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={onDelete}
                                disabled={hasReferencingAllocations}
                                className="flex items-center gap-2 text-red-600 focus:text-red-600"
                            >
                                <LuTrash2 className="h-4 w-4" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            <div className="cursor-pointer" onClick={onEdit}>
                <h3 className="text-cocoa-1000 truncate text-lg leading-[1.1] font-medium tracking-[-0.02em]">
                    {strategy.strategyName}
                </h3>
            </div>

            <div className="text-cocoa-700 mt-3 text-[13px] leading-[1.4]">
                <span className="text-cocoa-1000 font-medium">{getStrategyHoldingsLabel(strategy)}</span>
                {holdingsPreview.length > 0 && <> · {holdingsPreview.join(' · ')}</>}
                {hiddenHoldingsCount > 0 && <> · +{hiddenHoldingsCount} more</>}
            </div>

            <div className="border-cocoa-200/50 mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
                {badge && <TypeBadge label={badge.label} tone={badge.tone} small />}
                {isOwned && (
                    <span
                        className={`text-[13px] tabular-nums ${hasReferencingAllocations ? 'text-cocoa-1000' : 'text-cocoa-500'}`}
                    >
                        Used in {strategy.referencingAllocationCount ?? 0}{' '}
                        {strategy.referencingAllocationCount === 1 ? 'model' : 'models'}
                    </span>
                )}
            </div>
        </StackedCard>
    );
};
